// =====================================================
// IMPORTS
// =====================================================
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    getDoc,
    getDocs,
    doc,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    setDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';

// =====================================================
// FORM SERVICE FUNCTIONS
// =====================================================

/**
 * Create a new form
 */
const createForm = async (formData) => {
    try {
        const formsCollection = collection(db, 'forms');
        const docRef = await addDoc(formsCollection, {
            ...formData,
            createdAt: new Date(),
            archived: false,
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating form:', error);
        throw error;
    }
};

/**
 * Get a single form
 */
const getForm = async (formId) => {
    try {
        const formDoc = await getDoc(doc(db, 'forms', formId));
        if (formDoc.exists()) {
            return { id: formDoc.id, ...formDoc.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting form:', error);
        throw error;
    }
};

/**
 * Get all forms
 */
const getAllForms = async () => {
    try {
        const querySnapshot = await getDocs(collection(db, 'forms'));
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (err) {
        console.error('Error fetching all forms:', err);
        return [];
    }
};

/**
 * Get active forms (not archived)
 */
const getActiveForms = async () => {
    try {
        const formsCollection = collection(db, 'forms');
        const q = query(formsCollection, where('archived', '==', false));
        const formsSnapshot = await getDocs(q);
        return formsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching active forms:', error);
        return [];
    }
};

/**
 * Get recent forms
 */
const getRecentForms = async (limitCount = 10) => {
    try {
        const formsCollection = collection(db, 'forms');
        const q = query(
            formsCollection,
            where('archived', '==', false),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
        );
        const formsSnapshot = await getDocs(q);
        return formsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching recent forms:', error);
        return [];
    }
};

/**
 * Get forms by user ID
 */
const getFormsByUserId = async (userId) => {
    try {
        const formsCollection = collection(db, 'forms');
        const q = query(formsCollection, where('createdBy', '==', userId));
        const formsSnapshot = await getDocs(q);
        return formsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching user forms:', error);
        return [];
    }
};

/**
 * Update a form
 */
const updateForm = async (formId, formData) => {
    try {
        const formRef = doc(db, 'forms', formId);
        await updateDoc(formRef, {
            ...formData,
            updatedAt: new Date(),
        });
        return true;
    } catch (error) {
        console.error('Error updating form:', error);
        throw error;
    }
};

/**
 * Delete a form
 */
const deleteForm = async (formId) => {
    try {
        await deleteDoc(doc(db, 'forms', formId));
        return true;
    } catch (error) {
        console.error('Error deleting form:', error);
        throw error;
    }
};

/**
 * Publish a form (archive it)
 */
const publishForm = async (formId) => {
    try {
        const formRef = doc(db, 'forms', formId);
        await updateDoc(formRef, {
            ispublished: true,
            publishedAt: new Date(),
        });
        return true;
    } catch (error) {
        console.error('Error publishing form:', error);
        throw error;
    }
};

// Form Service Object
const formService = {
    createForm,
    getForm,
    getAllForms,
    getActiveForms,
    getRecentForms,
    getFormsByUserId,
    updateForm,
    deleteForm,
    publishForm,
};

// =====================================================
// SUBMISSION SERVICE FUNCTIONS
// =====================================================

/**
 * Create a new submission
 */
const createSubmission = async (submissionData) => {
    try {
        const submissionsCollection = collection(db, 'submissions');
        const docRef = await addDoc(submissionsCollection, {
            ...submissionData,
            submittedAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating submission:', error);
        throw error;
    }
};

/**
 * Get a single submission
 */
const getSubmission = async (submissionId) => {
    try {
        const submissionDoc = await getDoc(doc(db, 'submissions', submissionId));
        if (submissionDoc.exists()) {
            return { id: submissionDoc.id, ...submissionDoc.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting submission:', error);
        throw error;
    }
};

/**
 * Get all submissions
 */
const getAllSubmissions = async () => {
    try {
        const submissionsCollection = collection(db, 'submissions');
        const submissionsSnapshot = await getDocs(submissionsCollection);
        return submissionsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching all submissions:', error);
        return [];
    }
};

/**
 * Get submissions by user ID
 */
const getSubmissionsByUserId = async (userId) => {
    try {
        const submissionsCollection = collection(db, 'submissions');
        const q = query(submissionsCollection, where('userId', '==', userId));
        const submissionsSnapshot = await getDocs(q);
        return submissionsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching user submissions:', error);
        return [];
    }
};

/**
 * Get submissions by form ID
 */
const getSubmissionsByFormId = async (formId) => {
    try {
        const submissionsCollection = collection(db, 'submissions');
        const q = query(submissionsCollection, where('formId', '==', formId));
        const submissionsSnapshot = await getDocs(q);
        return submissionsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching form submissions:', error);
        return [];
    }
};

/**
 * Get form submissions (alias)
 */
const getFormSubmissions = async (formId) => {
    return getSubmissionsByFormId(formId);
};

/**
 * Get user submissions (alias)
 */
const getUserSubmissions = async (userId) => {
    return getSubmissionsByUserId(userId);
};

/**
 * Get submissions with scores
 */
const getSubmissionsWithScores = async () => {
    try {
        const submissionsCollection = collection(db, 'submissions');
        const q = query(submissionsCollection, where('scorePercentage', '>', 0));
        const submissionsSnapshot = await getDocs(q);
        return submissionsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching submissions with scores:', error);
        return [];
    }
};

/**
 * Update a submission
 */
const updateSubmission = async (submissionId, submissionData) => {
    try {
        const submissionRef = doc(db, 'submissions', submissionId);
        await updateDoc(submissionRef, {
            ...submissionData,
            updatedAt: new Date(),
        });
        return true;
    } catch (error) {
        console.error('Error updating submission:', error);
        throw error;
    }
};

/**
 * Delete a submission
 */
const deleteSubmission = async (submissionId) => {
    try {
        await deleteDoc(doc(db, 'submissions', submissionId));
        return true;
    } catch (error) {
        console.error('Error deleting submission:', error);
        throw error;
    }
};

// Submission Service Object
const submissionService = {
    createSubmission,
    getSubmission,
    getAllSubmissions,
    getSubmissionsByUserId,
    getSubmissionsByFormId,
    getFormSubmissions,
    getUserSubmissions,
    getSubmissionsWithScores,
    updateSubmission,
    deleteSubmission,
};

// =====================================================
// EXPORTS
// =====================================================

export { formService, submissionService };
export const updateDocument = async (collection, docId, data) => {
    try {
        const docRef = doc(db, collection, docId);
        await updateDoc(docRef, {
            ...data,
            // updatedAt: serverTimestamp(),
        });
        return true;
    } catch (err) {
        console.error('Error updating document:', err);
        throw err;
    }
};
export default {
    formService,
    submissionService,
};
